import React from 'react'
import { formatCurrency } from '@/lib/utils'
import type { DiscountType } from '@/lib/totals'

/**
 * Discount row for the QuoteDetail and InvoiceDetail totals panel.
 *
 * Visual pattern matches the totals rows above/below it (Subtotal / Tax / Total).
 * Pill picker for the three discount types (None / % / $) + a value input that
 * only renders when type !== 'none'. Computed monetary impact shown right-aligned
 * as a negative number — matches the email renderer's "−$10.00" convention.
 *
 * Locked mode (paid invoices, declined/won/invoiced quotes) renders read-only.
 */

interface Props {
  isLocked: boolean
  discountType: DiscountType
  discountValue: number
  discountAmount: number
  currency?: string
  onTypeChange: (t: DiscountType) => void
  onValueChange: (v: number) => void
}

const PILL_OPTIONS: Array<{ id: DiscountType; label: string }> = [
  { id: 'none',       label: '–' },
  { id: 'percentage', label: '%' },
  { id: 'fixed',      label: '$' },
]

export default function DiscountRow({
  isLocked, discountType, discountValue, discountAmount, currency,
  onTypeChange, onValueChange,
}: Props) {
  // Hide the row entirely when there's no discount and the editor is locked —
  // a paid invoice with no discount shouldn't carry a dead "Discount $0.00" line.
  if (isLocked && discountType === 'none') return null

  return (
    <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Discount</span>

        {!isLocked && (
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 6 }}>
            {PILL_OPTIONS.map(opt => {
              const active = discountType === opt.id
              return (
                <button key={opt.id} type="button"
                  onClick={() => onTypeChange(opt.id)}
                  aria-pressed={active}
                  aria-label={opt.id === 'none' ? 'No discount' : opt.id === 'percentage' ? 'Percentage discount' : 'Fixed amount discount'}
                  style={{ padding: '2px 8px', fontSize: 11, fontWeight: 700, borderRadius: 4, cursor: 'pointer', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: 'none', minWidth: 18 }}>
                  {opt.label}
                </button>
              )
            })}
          </div>
        )}

        {!isLocked && discountType !== 'none' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="number" value={discountValue}
              min={0}
              max={discountType === 'percentage' ? 100 : undefined}
              step={discountType === 'percentage' ? 0.1 : 0.01}
              onChange={e => {
                const raw = parseFloat(e.target.value) || 0
                const clamped = discountType === 'percentage' ? Math.min(Math.max(raw, 0), 100) : Math.max(raw, 0)
                onValueChange(clamped)
              }}
              style={{ width: 56, padding: '2px 6px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 5, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', textAlign: 'center' }} />
            <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>
              {discountType === 'percentage' ? '%' : '$'}
            </span>
          </div>
        )}

        {isLocked && discountType !== 'none' && (
          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>
            {discountType === 'percentage' ? `${discountValue}%` : `$${discountValue}`}
          </span>
        )}
      </div>

      <span style={{ fontSize: 13, fontWeight: 600, color: discountAmount > 0 ? 'var(--positive)' : 'var(--ink-primary)' }}>
        {discountAmount > 0 ? `−${formatCurrency(discountAmount, currency)}` : formatCurrency(0, currency)}
      </span>
    </div>
  )
}
